import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { Notification } from '@models/notifications/notification.model';
import { PriceSuggesterResultsInterface } from '@app/interfaces/priceSuggester/priceSuggester.interface.type';
import { Negotiation } from '@app/models/negotiation.model';

@Injectable({
  providedIn: 'root',
})
export class StateService {
  toggleWaitingPageSource = new Subject<{ status: boolean; data?: any }>();

  // Used after a task is created
  showDoneComponentSource: Subject<boolean> = new Subject<boolean>();

  // Used to show some information to the user
  showInfoComponentSource: Subject<any> = new Subject<any>();

  // Used to toggle a 'waiting state' that the platform is in
  toggleWaitingComponentSource: Subject<boolean> = new Subject<boolean>();

  // Used to toggle an extended 'waiting state' that the platform is in with more data
  // for the user than the basic toggleWaitingComponentSource
  toggleExtendedWaitingComponentSource = new BehaviorSubject<{
    status: boolean;
    title?: any;
    steps?: any[];
    hasError?: boolean;
  }>({ status: false });

  priceSuggesterDataSource: BehaviorSubject<PriceSuggesterResultsInterface> =
    new BehaviorSubject<PriceSuggesterResultsInterface>({
      uuid: null,
      isFallbackApplicable: false,
      priceSuggesterAccuracyScore: 0,
      AIEntityData: undefined,
      fallBackBudgetByTagHourlyWage: 0,
      predictedTotalBudget: 0,
      successStatus: false,
    });

  // Boolean for checking auth state
  isAuthenticatedStateSource: Subject<boolean> = new Subject<boolean>();

  // Used to open the sidebar
  showSidebarComponentSource: Subject<number> = new Subject<number>();

  // Used to close the sidebar
  closeSidebarComponentSource: Subject<void> = new Subject<void>();

  // Used to show new messages in the header
  showNewMessagesHeaderComponentSource: Subject<number> = new Subject<number>();

  // Used to show new notifications in the header
  showNewNotificationsHeaderComponentSource: Subject<number> =
    new Subject<number>();

  // Used to init new notifications in the header
  initNewNotificationsHeaderComponentSource: Subject<number> =
    new Subject<number>();

  // Add new notification from socket to Notifications in Sidebar
  addNewNotificationToSidebarComponentSource: Subject<Notification> =
    new Subject<Notification>();

  // Used to read a notification from header
  readNotificationComponentSource: Subject<void> = new Subject<void>();

  // Used to show live updates of a task, when on that respective task's page
  fetchTaskDetailsComponentSource: Subject<void> = new Subject<void>();

  // Used to show live update of a offer comment
  fetchOfferCommentDetailComponentSource: Subject<any> = new Subject<any>();

  // Used to automatically open review dialog after task is marked as completed
  openReviewDialogComponentSource: Subject<void> = new Subject<void>();

  // Used to hide both header and footer on mobile
  hideHeaderFooterComponentSource: Subject<boolean> = new Subject<boolean>();

  // Used to init user language
  initLanguageSource: Subject<any> = new Subject<any>();

  // Used to trigger the validity check in parent component
  checkValiditySource: Subject<string> = new Subject<string>();

  // Subscribe to task if updated
  showUpdatedTaskComponentSource: Subject<number> = new Subject<number>();

  // Subscribe to comment if updated
  fetchCommentComponentSource: Subject<any> = new Subject<any>();

  // Check if new offer
  showNewOfferComponentSource: Subject<any> = new Subject<any>();

  showUpdatedOfferComponentSource: Subject<number> = new Subject<number>();

  showCancelledOfferComponentSource: Subject<number> = new Subject<number>();

  // Check if task description is blurred
  isTaskDescriptionBlurredSource: Subject<boolean> = new Subject<boolean>();

  // Scroll to bottom after component is rendered
  scrollToBottomSource: Subject<void> = new Subject<void>();

  scrollToElementSource: Subject<string> = new Subject<string>();

  showHeaderSource: Subject<boolean> = new Subject<boolean>();

  public createOrUpdateNegotiationSource: Subject<Negotiation> =
    new Subject<Negotiation>();

  public supportTicketUpdatedSource: Subject<any> = new Subject<any>();

  supportTicketUpdate(data: any) {
    this.supportTicketUpdatedSource.next(data);
  }

  showHeader(status: boolean) {
    this.showHeaderSource.next(status);
  }

  showUpdatedTask(taskId: number) {
    this.showUpdatedTaskComponentSource.next(taskId);
  }

  showNewOffer(data: any) {
    this.showNewOfferComponentSource.next(data);
  }

  showUpdatedOffer(offerId: number) {
    this.showUpdatedOfferComponentSource.next(offerId);
  }

  showCancelledOffer(offerId: number) {
    this.showCancelledOfferComponentSource.next(offerId);
  }

  showUpdatedComment(data: any) {
    this.fetchCommentComponentSource.next(data);
  }

  showUpdatedOfferComment(data: any) {
    this.fetchOfferCommentDetailComponentSource.next(data);
  }

  triggerIsTaskDetailDescBlurred(data: any) {
    this.isTaskDescriptionBlurredSource.next(data);
  }

  showInfoComponent(data: any) {
    this.showInfoComponentSource.next(data);
  }

  toggleWaitingPage(status: boolean) {
    this.toggleWaitingComponentSource.next(status);
  }

  toggleWaitingPageExtended(
    status: boolean,
    title?: any,
    steps?: any[],
    hasError?: boolean,
  ) {
    this.toggleExtendedWaitingComponentSource.next({
      status,
      title,
      steps,
      hasError,
    });
  }

  checkAuthState(status: boolean) {
    this.isAuthenticatedStateSource.next(status);
  }

  showSidebar(activeTab: number) {
    this.showSidebarComponentSource.next(activeTab);
  }

  closeSidebar() {
    this.closeSidebarComponentSource.next();
  }

  fetchTaskDetails() {
    this.fetchTaskDetailsComponentSource.next();
  }

  showNewMessagesHeader(count) {
    this.showNewMessagesHeaderComponentSource.next(count);
  }

  addNotificationToSidebar(item: Notification) {
    this.addNewNotificationToSidebarComponentSource.next(item);
  }

  showNewNotificationsHeader(count: number) {
    this.showNewNotificationsHeaderComponentSource.next(count);
  }

  initNewNotificationsHeader(count: number) {
    this.initNewNotificationsHeaderComponentSource.next(count);
  }

  readNotification() {
    this.readNotificationComponentSource.next();
  }

  openReviewDialog() {
    this.openReviewDialogComponentSource.next();
  }

  hideHeaderFooter(status: boolean) {
    this.hideHeaderFooterComponentSource.next(status);
  }

  initLanguage(data: any) {
    this.initLanguageSource.next(data);
  }

  triggerCheckValidity(id: string) {
    this.checkValiditySource.next(id);
  }

  scrollToBottom() {
    this.scrollToBottomSource.next();
  }

  scrollToElement(elementId: string) {
    this.scrollToElementSource.next(elementId);
  }

  updatePriceSuggesterData(data: any) {
    this.priceSuggesterDataSource.next(data);
  }

  createOrUpdateNegotiation(data: Negotiation) {
    this.createOrUpdateNegotiationSource.next(data);
  }
}
